import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import StyledInput from '../../components/Form/StyledInput';
import { CustomButton } from '../../components/Buttons';
import { callBackbonePostApi } from '../../services/backboneApiService';
import { getUser } from '../../utils/cookie';

import {
  EVENT_ACTIONS,
  EVENT_COLORS,
  EVENT_COPY,
  EVENT_ERROR_CODES,
  EVENT_ERROR_MESSAGES,
  RSVP_CHOICES,
} from './constants';
import {
  buildIdentityRequest,
  buildRsvpRequest,
  buildStoredGuest,
  canChangeRsvp,
  clearGuestRsvp,
  describeRsvp,
  extractErrorMessage,
  getErrorCode,
  isActiveRsvp,
  isSoftFailure,
  loadGuestRsvp,
  saveGuestRsvp,
  validateGuestDetails,
} from './helpers';

const REFRESH_ERROR_CODES = [
  EVENT_ERROR_CODES.EVENT_CLOSED,
  EVENT_ERROR_CODES.EVENT_CANCELLED,
  EVENT_ERROR_CODES.EVENT_STARTED,
  EVENT_ERROR_CODES.EVENT_FULL,
  EVENT_ERROR_CODES.EVENT_NOT_FOUND,
];

const RsvpForm = ({ event, availability, onEventChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const eventId = event?.eventId;
  const user = getUser();
  const isMember = Boolean(user?.userId);

  const [stored, setStored] = useState(() => (isMember ? null : loadGuestRsvp(eventId)));
  const [rsvp, setRsvp] = useState(null);
  const [guest, setGuest] = useState(() => ({
    name: stored?.name || '',
    email: stored?.email || '',
    phone: stored?.phone || '',
  }));
  const [errors, setErrors] = useState({});
  const [pendingStatus, setPendingStatus] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const isEditable = canChangeRsvp(availability);
  const isBusy = Boolean(pendingStatus) || isCancelling;
  const guestsBlocked = !isMember && event?.allowGuests === false;

  useEffect(() => {
    const identity = buildIdentityRequest({ eventId, user, stored });
    if (!identity) {
      return undefined;
    }
    let isActive = true;
    callBackbonePostApi(
      { action: EVENT_ACTIONS.GET_RSVP, ...identity.payload },
      '',
      identity.config
    )
      .then((response) => {
        if (isActive && response?.data?.success) {
          setRsvp(response.data.rsvp || null);
        }
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleError = useCallback(
    (failure) => {
      enqueueSnackbar(extractErrorMessage(failure), { variant: 'error' });
      if (REFRESH_ERROR_CODES.includes(getErrorCode(failure))) {
        onEventChanged?.();
      }
    },
    [enqueueSnackbar, onEventChanged]
  );

  const handleGuestChange = (field) => (changeEvent) => {
    setGuest((current) => ({ ...current, [field]: changeEvent.target.value }));
  };

  const submitRsvp = async (status) => {
    if (!isMember) {
      const validation = validateGuestDetails(guest);
      setErrors(validation.errors);
      if (!validation.isValid) {
        return;
      }
    }

    const { payload, config } = buildRsvpRequest({ eventId, status, user, guest, stored });
    setPendingStatus(status);
    try {
      const response = await callBackbonePostApi(
        { action: EVENT_ACTIONS.RSVP, ...payload },
        '',
        config
      );
      if (isSoftFailure(response) || !response?.data?.success) {
        handleError(response);
        return;
      }
      setRsvp(response.data.rsvp || null);
      if (!isMember) {
        const nextStored =
          buildStoredGuest({ guest, response }) || (stored && { ...stored, ...guest });
        if (nextStored) {
          saveGuestRsvp(eventId, nextStored);
          setStored(nextStored);
        }
      }
      enqueueSnackbar(
        response.data.waitlisted ? EVENT_COPY.RSVP_WAITLISTED : EVENT_COPY.RSVP_SAVED,
        { variant: response.data.waitlisted ? 'info' : 'success' }
      );
      onEventChanged?.();
    } catch (error) {
      handleError(error);
    } finally {
      setPendingStatus('');
    }
  };

  const cancelRsvp = async () => {
    const identity = buildIdentityRequest({ eventId, user, stored });
    if (!identity) {
      return;
    }
    setIsCancelling(true);
    try {
      const response = await callBackbonePostApi(
        { action: EVENT_ACTIONS.CANCEL_RSVP, ...identity.payload },
        '',
        identity.config
      );
      if (isSoftFailure(response) || !response?.data?.success) {
        handleError(response);
        return;
      }
      setRsvp(null);
      if (!isMember) {
        clearGuestRsvp(eventId);
        setStored(null);
      }
      enqueueSnackbar(EVENT_COPY.RSVP_CANCELLED, { variant: 'success' });
      onEventChanged?.();
    } catch (error) {
      handleError(error);
    } finally {
      setIsCancelling(false);
    }
  };

  const hasRsvp = isActiveRsvp(rsvp);
  const description = describeRsvp(rsvp);

  if (guestsBlocked) {
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        {EVENT_ERROR_MESSAGES[EVENT_ERROR_CODES.GUESTS_NOT_ALLOWED]}{' '}
        <RouterLink to="/signin" style={{ color: 'inherit', fontWeight: 700 }}>
          Log in
        </RouterLink>
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2, md: 3 },
        background: EVENT_COLORS.panel,
        border: `1px solid ${EVENT_COLORS.border}`,
        borderRadius: '8px',
      }}
    >
      <Typography fontWeight={700} fontSize={20} mb={1}>
        {hasRsvp ? 'Your RSVP' : 'RSVP'}
      </Typography>
      {description && (
        <Typography sx={{ color: EVENT_COLORS.good, mb: 2 }} data-testid="rsvp-description">
          {description}
        </Typography>
      )}

      {!isEditable && (
        <Typography sx={{ color: EVENT_COLORS.muted }}>
          RSVPs can no longer be changed for this event.
        </Typography>
      )}

      {isEditable && (
        <>
          {!isMember && (
            <Stack spacing={2} mb={2}>
              <Box>
                <StyledInput
                  label="Name"
                  value={guest.name}
                  onChange={handleGuestChange('name')}
                  autoComplete="name"
                  maxLength={120}
                />
                {errors.name && <FieldError>{errors.name}</FieldError>}
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box flex={1}>
                  <StyledInput
                    label="Email"
                    type="email"
                    value={guest.email}
                    onChange={handleGuestChange('email')}
                    autoComplete="email"
                  />
                  {errors.email && <FieldError>{errors.email}</FieldError>}
                </Box>
                <Box flex={1}>
                  <StyledInput
                    label="Phone"
                    type="tel"
                    value={guest.phone}
                    onChange={handleGuestChange('phone')}
                    autoComplete="tel"
                  />
                  {errors.phone && <FieldError>{errors.phone}</FieldError>}
                </Box>
              </Stack>
              {errors.contact && <FieldError>{errors.contact}</FieldError>}
              <Typography sx={{ color: EVENT_COLORS.muted, fontSize: 13 }}>
                Or{' '}
                <RouterLink to="/signin" style={{ color: EVENT_COLORS.accent }}>
                  log in
                </RouterLink>{' '}
                to RSVP with your Black Box VR account.
              </Typography>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {RSVP_CHOICES.map((choice) => {
              const isCurrent = hasRsvp && rsvp.status === choice.status;
              return (
                <CustomButton
                  key={choice.status}
                  variant={isCurrent ? 'contained' : 'outlined'}
                  loading={pendingStatus === choice.status}
                  disabled={isBusy}
                  onClick={() => submitRsvp(choice.status)}
                  sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}
                >
                  {choice.label}
                </CustomButton>
              );
            })}
          </Stack>

          {hasRsvp && (
            <Button
              color="error"
              disabled={isBusy}
              onClick={cancelRsvp}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Cancel my RSVP
            </Button>
          )}
        </>
      )}

      {!isMember && stored?.cancelToken && hasRsvp && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: EVENT_COLORS.muted, fontSize: 13 }}>
            {EVENT_COPY.GUEST_TOKEN_NOTE}
          </Typography>
          <Typography
            data-testid="guest-cancel-token"
            sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', mt: 0.5 }}
          >
            {stored.cancelToken}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const FieldError = ({ children }) => (
  <Typography sx={{ color: EVENT_COLORS.danger, fontSize: 13, mt: 0.5 }} role="alert">
    {children}
  </Typography>
);

export default RsvpForm;
